import Captcha from "@/common/components/Captcha";
import GoogleSignInButton from "@/common/components/GoogleSignInButton";
import SEO from "@/common/components/seo";
import LogoIcon from "@/common/svgs/logo";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { HomeRoute } from "@/features/home/route";
import { Link } from "react-router-dom";
import ActivateAccountDialog from "../containers/activate-account-dialog";
import useSignUp from "../hooks/use-sign-up";
import { LoginRoute } from "../route";
import CompactPlanPicker from "@/features/payments/containers/compact-plan-picker";
import InlineStripeCheckout from "@/features/payments/containers/inline-stripe-checkout";
import { CouponField } from "@/features/payments/pages/checkout";
import { useSiteSettings } from "@/common/providers/site-settings";
import { Plan } from "@/features/payments/models/plan";
import {
	PrivacyPolicyRoute,
	TermsAndConditionsRoute,
} from "@/features/legal-information/route";

export default function SignUpPage() {
	const {
		step,

		selectedPlan,
		setSelectedPlan,

		onSubmit,
		onInvalidSubmit,
		form,

		onResendVerificationCode,
		activateAccountForm,
		onActivateAccountSubmit,
		showActivateAccountDialog,
		setShowActivateAccountDialog,

		checkoutClientSecret,
		isCreatingCheckout,
		onContinueToCheckout,
		onSkipPlan,
		onCheckoutComplete,
		onBackToPlans,
		appliedPromotionCode,
		applyPromotionCode,
		removePromotionCode,

		isSubmitting,

		captchaToken,
		setCaptchaToken,

		onGoogleCredential,
		acceptedTerms,

	} = useSignUp();

	const { settings: siteSettings } = useSiteSettings();

	return (
		<div
			className="flex h-full min-h-screen w-full items-center justify-center px-4 py-10"
			style={{
				background:
					"radial-gradient(196.52% 110.66% at 52.19% 30.42%, rgba(46, 219, 202, 0.30) 0%, rgba(8, 50, 46, 0.00) 100%), radial-gradient(321.2% 194.09% at 77.63% -11.3%, #2EDBCA 4.81%, #004096 39.75%, #470096 100%)",
				backgroundBlendMode: "plus-lighter, normal",
			}}
		>
			<SEO
				title="Sign Up - Reel Rewards"
				description="Create a Reel Rewards account to unlock partner discounts, enter giveaways, and manage your membership."
			/>
			<ActivateAccountDialog
				form={activateAccountForm}
				onSubmit={onActivateAccountSubmit}
				showActivateAccountDialog={showActivateAccountDialog}
				setShowActivateAccountDialog={setShowActivateAccountDialog}
				onResend={onResendVerificationCode}
			/>

			<div className="flex w-full max-w-[480px] flex-col gap-6">
				<Link to={HomeRoute} className="w-fit">
					<LogoIcon height={56} width={53} />
				</Link>

				<StepIndicator step={step} />

				{step === "details" && (
					<DetailsStep
						form={form}
						onSubmit={onSubmit}
						onInvalidSubmit={onInvalidSubmit}
						isSubmitting={isSubmitting}
						captchaToken={captchaToken}
						setCaptchaToken={setCaptchaToken}
						acceptedTerms={acceptedTerms}
						onGoogleCredential={onGoogleCredential}
					/>
				)}

				{step === "plans" && !checkoutClientSecret && (
					<PlansStep
						selectedPlan={selectedPlan}
						setSelectedPlan={setSelectedPlan}
						isCreatingCheckout={isCreatingCheckout}
						onContinueToCheckout={onContinueToCheckout}
						onSkipPlan={onSkipPlan}
					/>
				)}

				{step === "plans" && checkoutClientSecret && (
					<div className="flex flex-col gap-4">
						{selectedPlan && (
							<CheckoutPlanSummary
								plan={selectedPlan}
								appliedPromotionCode={appliedPromotionCode}
							/>
						)}
						{siteSettings.subscriptionCouponCodesEnabled && (
							<CouponField
								applied={appliedPromotionCode}
								isLoading={isCreatingCheckout}
								onApply={applyPromotionCode}
								onRemove={removePromotionCode}
							/>
						)}
						<InlineStripeCheckout
							key={checkoutClientSecret}
							clientSecret={checkoutClientSecret}
							mode="subscription"
							addressMode="billing"
							confirmLabel="Pay & subscribe"
							cancelLabel="Change plan"
							onComplete={onCheckoutComplete}
							onCancel={onBackToPlans}
						/>
					</div>
				)}
			</div>
		</div>
	);
}

function StepIndicator({ step }: { step: "details" | "plans" }) {
	return (
		<div className="flex items-center gap-2">
			<div
				className={`h-1.5 flex-1 rounded-full ${
					step === "details" ? "bg-white" : "bg-white/40"
				}`}
			/>
			<div
				className={`h-1.5 flex-1 rounded-full ${
					step === "plans" ? "bg-white" : "bg-white/40"
				}`}
			/>
		</div>
	);
}

function DetailsStep({
	form,
	onSubmit,
	onInvalidSubmit,
	isSubmitting,
	captchaToken,
	setCaptchaToken,
	acceptedTerms,
	onGoogleCredential,
}: {
	form: ReturnType<typeof useSignUp>["form"];
	onSubmit: ReturnType<typeof useSignUp>["onSubmit"];
	onInvalidSubmit: ReturnType<typeof useSignUp>["onInvalidSubmit"];
	isSubmitting: boolean;
	captchaToken: ReturnType<typeof useSignUp>["captchaToken"];
	setCaptchaToken: ReturnType<typeof useSignUp>["setCaptchaToken"];
	acceptedTerms: ReturnType<typeof useSignUp>["acceptedTerms"];
	onGoogleCredential: ReturnType<typeof useSignUp>["onGoogleCredential"];
}) {
	return (
		<>
			<div className="flex flex-col gap-2">
				<p className="web-heading-h3 text-white">Create your account</p>
				<p className="web-body-b1 text-white-80">
					Just the basics — you can add the rest from your profile later.
				</p>
			</div>

			<div className="flex flex-col gap-3">
				<GoogleSignInButton
					onAccessToken={onGoogleCredential}
					label="Sign up with Google"
					disabled={!acceptedTerms}
				/>
				<div className="flex items-center gap-3">
					<div className="h-px flex-1 bg-white/30" />
					<span className="web-body-b3 text-white/70">or</span>
					<div className="h-px flex-1 bg-white/30" />
				</div>
			</div>

			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(onSubmit, onInvalidSubmit)}
					className="flex flex-col gap-4"
				>
					<FormField
						control={form.control}
						name="email"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-white-80">Email</FormLabel>
								<FormControl>
									<Input
										{...field}
										required
										className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										type="email"
										placeholder="Email address"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="password"
						render={({ field }) => (
							<FormItem>
								<FormLabel className="text-white-80">Password</FormLabel>
								<FormControl>
									<Input
										{...field}
										required
										type="password"
										placeholder="Password"
										className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										eyeColor="text-white"
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="grid grid-cols-2 gap-4">
						<FormField
							control={form.control}
							name="firstName"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-white-80">First name</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											placeholder="First name"
											className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="lastName"
							render={({ field }) => (
								<FormItem>
									<FormLabel className="text-white-80">Last name</FormLabel>
									<FormControl>
										<Input
											{...field}
											required
											placeholder="Last name"
											className="bg-blue-900-20 text-white placeholder:text-white placeholder:opacity-70"
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<FormField
						control={form.control}
						name="acceptedTerms"
						render={({ field }) => (
							<FormItem>
								<div className="flex items-center gap-2">
									<FormControl>
										<input
											type="checkbox"
											id="accepted-terms"
											checked={field.value === true}
											onChange={(e) => field.onChange(e.target.checked)}
											onBlur={field.onBlur}
											ref={field.ref}
											className="h-4 w-4 shrink-0 cursor-pointer rounded border-white/40 bg-blue-900-20 accent-white"
										/>
									</FormControl>
									<label
										htmlFor="accepted-terms"
										className="web-body-b3 cursor-pointer text-white-80"
									>
										I agree to the{" "}
										<Link
											to={TermsAndConditionsRoute}
											target="_blank"
											rel="noopener noreferrer"
											className="text-white underline"
										>
											Terms and Conditions
										</Link>{" "}
										and{" "}
										<Link
											to={PrivacyPolicyRoute}
											target="_blank"
											rel="noopener noreferrer"
											className="text-white underline"
										>
											Privacy Policy
										</Link>
										.
									</label>
								</div>
								<FormMessage />
							</FormItem>
						)}
					/>

					<div className="mt-2 flex justify-center">
						<Captcha
							onVerify={setCaptchaToken}
							options={{ size: "flexible" }}
						/>
					</div>

					<Button
						type="submit"
						variant="white"
						disabled={!captchaToken || isSubmitting}
					>
						{isSubmitting ? "Signing up…" : "Sign up"}
					</Button>

					<span className="web-body-b1 w-full text-center text-white">
						Already a member?{" "}
						<Link to={LoginRoute} className="web-button text-white">
							Sign In
						</Link>
					</span>
				</form>
			</Form>
		</>
	);
}

function PlansStep({
	selectedPlan,
	setSelectedPlan,
	isCreatingCheckout,
	onContinueToCheckout,
	onSkipPlan,
}: {
	selectedPlan: ReturnType<typeof useSignUp>["selectedPlan"];
	setSelectedPlan: ReturnType<typeof useSignUp>["setSelectedPlan"];
	isCreatingCheckout: boolean;
	onContinueToCheckout: () => void;
	onSkipPlan: () => void;
}) {
	return (
		<div className="flex flex-col gap-6">
			<div className="flex flex-col gap-2">
				<p className="web-heading-h3 text-white">Pick a membership</p>
				<p className="web-body-b1 text-white-80">
					Optional. You can join or upgrade anytime.
				</p>
			</div>

			<CompactPlanPicker
				selectedPlanId={selectedPlan?.id}
				onSelect={setSelectedPlan}
			/>

			<div className="flex flex-col items-center gap-5">
				<Button
					variant="white"
					disabled={!selectedPlan || isCreatingCheckout}
					onClick={onContinueToCheckout}
				>
					{isCreatingCheckout ? "Loading…" : "Continue to checkout"}
				</Button>
				<button
					type="button"
					onClick={onSkipPlan}
					className="web-body-b1 text-white/80 underline-offset-4 hover:text-white hover:underline"
				>
					Skip for now
				</button>
			</div>
		</div>
	);
}

function CheckoutPlanSummary({
	plan,
	appliedPromotionCode,
}: {
	plan: Plan;
	appliedPromotionCode: string | null;
}) {
	return (
		<div className="rounded-2xl border border-white/20 bg-white/10 px-4 py-4 text-white">
			<div className="flex items-start justify-between gap-4">
				<div className="flex flex-col gap-0.5">
					<p className="web-body-b1-medium">{plan.name}</p>
					<p className="web-body-b3 text-white/70">
						{plan.monthlyEntries} entries / month &middot;{" "}
						{plan.weeklyEntries} weekly draw entries
					</p>
				</div>
				<div className="shrink-0 text-right">
					<p className="web-body-b1-medium">${Number(plan.price).toFixed(2)}</p>
					<p className="web-body-b3 text-white/70">/ month</p>
				</div>
			</div>
			{appliedPromotionCode && (
				<p className="web-body-b3 mt-2 text-green-300">
					Coupon <strong>{appliedPromotionCode}</strong> applied
				</p>
			)}
			<p className="web-body-b3 mt-2 text-white/50">
				Cancel anytime from your profile.
			</p>
		</div>
	);
}
